/**
 * Comparar el personal del negocio.
 *
 * Existe para una sola pregunta, la misma que responde lib/horarios.js para la
 * grilla: ¿lo que está en pantalla dice algo distinto de lo último que se
 * guardó? De eso depende que el panel avise "tenés cambios sin guardar" antes
 * de dejarte salir.
 *
 * No alcanza con `JSON.stringify(a) === JSON.stringify(b)`. Las personas pasan
 * por formularios que reescriben el objeto entero —el alta arma uno nuevo, la
 * edición hace spread sobre el anterior, borrar un rol vuelve a armar `roles` y
 * `rol`—, así que el orden de las claves y los campos que el objeto arrastre
 * (timestamps del backend, banderas de la pantalla) cambian sin que cambie
 * nada de lo que el negocio ve. Comparando el texto crudo, el panel avisaría
 * por cambios que no existen, y un aviso que salta cuando no corresponde se
 * aprende a cerrar sin leer.
 *
 * Se compara sólo lo que se manda a `POST /api/sync/staff` y termina afectando
 * a un turno: quién es, cómo se lo puede contactar, qué hace, si está activo y
 * cuándo trabaja.
 */

import { mismoHorario } from './horarios';
import { normalizarRol, rolesDe } from './roles';

/**
 * Los roles en una sola línea, ordenados.
 *
 * Ordenados porque el orden no significa nada: alguien que apaga "Cardiólogo" y
 * lo vuelve a prender lo deja al final de la lista, y eso no es un cambio en
 * lo que esa persona hace. Normalizados por lo mismo que en lib/roles.js —
 * "Cardiólogo" y "cardiologo" son el mismo rol.
 */
const resumirRoles = (persona) =>
  rolesDe(persona).map(normalizarRol).filter(Boolean).sort().join('|');

/** Una persona reducida a lo que el negocio ve de ella. */
const resumirPersona = (persona) => ({
  id: String(persona?.id ?? ''),
  nombre: String(persona?.name ?? '').trim(),
  email: String(persona?.email ?? '').trim(),
  telefono: String(persona?.phone ?? '').trim(),
  roles: resumirRoles(persona),
  // Cambiar la foto también es un cambio sin guardar: el archivo ya está en
  // Storage apenas se elige, pero la persona no apunta a él hasta que se
  // guarda, y sin esto irse del panel dejaba la foto subida y a nadie usándola.
  foto: String(persona?.foto ?? ''),
  // `active` puede venir sin definir en gente cargada antes de que el campo
  // existiera, y ahí el panel la muestra como activa: si acá contara como
  // distinta de `true`, esas personas darían "cambio sin guardar" con sólo
  // abrir la pantalla.
  activa: persona?.active !== false,
  horario: persona?.schedule || null,
});

/** ¿Las dos listas de personal dicen lo mismo? */
export const mismoPersonal = (a, b) => {
  const unos = Array.isArray(a) ? a : [];
  const otros = Array.isArray(b) ? b : [];

  if (unos.length !== otros.length) return false;

  // Se compara en orden y no por id: el orden de la lista es el que se ve en
  // pantalla y el que se guarda, así que mover a alguien de lugar ES un cambio.
  return unos.every((persona, i) => {
    const uno = resumirPersona(persona);
    const otro = resumirPersona(otros[i]);

    return (
      uno.id === otro.id &&
      uno.nombre === otro.nombre &&
      uno.email === otro.email &&
      uno.telefono === otro.telefono &&
      uno.roles === otro.roles &&
      uno.foto === otro.foto &&
      uno.activa === otro.activa &&
      // El horario propio de cada persona se compara con la misma regla que el
      // del negocio, para que un día cerrado con rangos guardados no cuente
      // como distinto de un día cerrado sin ellos.
      mismoHorario(uno.horario, otro.horario)
    );
  });
};
